import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { ControlledSelectField, ControlledTextField, ControlNumberField } from 'components/Form';
import { ModalDialog } from 'components/ModalDialog';
import { RequestStatus } from 'features/request/requestSlice';
import { RatioTypeEnum, type CatalogRatioCreateInput, type CatalogRatioUpdateInput } from 'gql/graphql';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';
import type { AnyObject } from 'types/AnyObject';
import { Grid, IconWarning, Informer } from 'uikit';
import { requiredBaseRule } from 'utils/constants';

import { GridItem } from '@consta/uikit/Grid';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import type {
    FieldValuesRatioCreateOrEditStructure,
    RatioIdType,
    TransformedValuesRatioCreateOrEditStructure,
} from '../types';

import { DefaultFromValues } from './constants';
import {
    useComposeResponseValidationRules,
    useCreateRatio,
    useGroupsData,
    useRatio,
    useTypesData,
    useUpdateRatio,
} from './hooks';
import {
    createCatalogRatioInput,
    getCatalogRatioGroupIdRules,
    getNumericRatioFieldRules,
    getRatioTypeLabel,
    validateJustification,
} from './utils';

import Styles from './createOrEditRatioModal.module.css';

const isInputForUpdateRatio = (
    input: CatalogRatioCreateInput | CatalogRatioUpdateInput,
): input is CatalogRatioUpdateInput => 'id' in input;

type CreateOrEditRatioModalProps = {
    id: Exclude<RatioIdType, undefined>;
    onClose: () => void;
    refreshData: () => void;
};

export const CreateOrEditRatioModal = ({ id, onClose, refreshData }: CreateOrEditRatioModalProps) => {
    const [isRatioTypesDisabled, setIsRatioTypesDisabled] = useState<boolean>(false);
    const [isRatioGroupsDisabled, setIsRatioGroupsDisabled] = useState<boolean>(true);

    const title = useMemo(() => (id ? 'Редактирование Коэффициента' : 'Создание Коэффициента'), [id]);

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: onClose,
        });

    const { control, handleSubmit, formState, setValue, trigger, clearErrors } = useForm<
        FieldValuesRatioCreateOrEditStructure,
        AnyObject,
        TransformedValuesRatioCreateOrEditStructure
    >({
        criteriaMode: 'all',
        shouldUnregister: true,
        defaultValues: DefaultFromValues,
    });

    const ratioType = useWatch<FieldValuesRatioCreateOrEditStructure, 'ratioType'>({ control, name: 'ratioType' });

    const { ratioData, ratioStatus } = useRatio(id);

    const { typesData, typesStatus } = useTypesData(!id);
    const { groupsData, groupsStatus } = useGroupsData({ ratioTypeId: ratioType?.id, groupsEffect: !id });
    const {
        createRatioHandler,
        createRatioStatus,
        resetCreateData,
        validationMessages: createValidationMessages,
    } = useCreateRatio({
        closeModalHandler: onClose,
        refreshData,
    });
    const {
        updateRatioHandler,
        updateRatioStatus,
        resetUpdateData,
        validationMessages: updateValidationMessages,
    } = useUpdateRatio({
        closeModalHandler: onClose,
        refreshData,
    });

    const { validationRules, updateInput } = useComposeResponseValidationRules({
        validationMessages: id ? updateValidationMessages : createValidationMessages,
    });

    const typesItems = useMemo(
        () =>
            typesData.map((type) => ({
                id: type,
                label: getRatioTypeLabel(type),
            })),
        [typesData],
    );

    const groupsItems = useMemo(() => groupsData.map((group) => ({ id: group.id, label: group.name })), [groupsData]);

    useEffect(() => {
        if (ratioData) {
            setValue('name', ratioData.name);
            if (ratioData.catalogRatioGroupId) {
                setValue('catalogRatioGroupId', {
                    label: ratioData.catalogRatioGroupName ?? '',
                    id: ratioData.catalogRatioGroupId,
                });
            }

            setValue('ratioType', { label: getRatioTypeLabel(ratioData.ratioType), id: ratioData.ratioType });
            setValue('code', ratioData.code);
            setValue('kozp', ratioData.kozp);
            setValue('kem', ratioData.kem);
            setValue('knr', ratioData.knr);
            setValue('ksp', ratioData.ksp);
            setValue('kmat', ratioData.kmat);
            setValue('ktz', ratioData.ktz);
            setValue('ktzzpm', ratioData.ktzzpm);
        }
    }, [setValue, ratioData]);

    useEffect(() => {
        if (typesStatus === RequestStatus.SUCCESS) {
            setIsRatioTypesDisabled(!typesData.length);
        }
    }, [typesData.length, typesStatus]);

    useEffect(() => {
        if (groupsStatus === RequestStatus.SUCCESS) {
            setIsRatioGroupsDisabled(!groupsData.length);
        }
    }, [groupsData.length, groupsStatus]);

    useEffect(() => {
        setValue('catalogRatioGroupId', undefined);
    }, [ratioType, setValue]);

    useEffect(() => {
        if (Object.keys(validationRules).length) {
            trigger();
        }
    }, [trigger, validationRules]);

    useEffect(() => {
        if (ratioType?.id === RatioTypeEnum.DismantlingCoef && !id) {
            setValue('kmat', '0.00');
        }
    }, [ratioType?.id, setValue, id]);

    const onModalClose = useCallback(() => {
        if (formState?.isDirty) {
            handleOpenConfirmationDialog();
        } else {
            onClose();
        }
    }, [formState?.isDirty, handleOpenConfirmationDialog, onClose]);

    const onSubmit = useCallback(
        (formData: TransformedValuesRatioCreateOrEditStructure) => {
            const input = createCatalogRatioInput(formData, id);

            clearErrors();

            if (isInputForUpdateRatio(input)) {
                resetUpdateData(undefined);
                updateRatioHandler(input);
            } else {
                resetCreateData(undefined);
                createRatioHandler(input);
            }
            updateInput(input);
        },
        [id, clearErrors, updateInput, resetUpdateData, updateRatioHandler, resetCreateData, createRatioHandler],
    );

    const catalogRatioGroupIdRules = useMemo(() => getCatalogRatioGroupIdRules(ratioType?.id), [ratioType?.id]);
    const numericRatioFieldRules = useMemo(() => getNumericRatioFieldRules(), []);
    const justificationRules = useMemo(() => validateJustification(), []);

    const isDismantlingCoef = ratioType?.id === RatioTypeEnum.DismantlingCoef;

    return (
        <ModalDialog
            isOpen
            isLoading={!!id && ratioStatus === RequestStatus.FETCHING}
            isCommit={[createRatioStatus, updateRatioStatus].includes(RequestStatus.FETCHING)}
            title={title}
            onClose={onModalClose}
            onConfirm={handleSubmit(onSubmit)}
            btnConfirmLabel={id ? 'Сохранить' : 'Создать'}
            btnConfirmDisabled={!formState.isDirty}
            bodyWidth={600}
            withTitleCloseButton
            isOnClickOutsideDisable
        >
            <div className={Styles.modalBody}>
                {!id && (
                    <Informer
                        size="s"
                        status="warning"
                        icon={IconWarning}
                        label="Новый коэффициент будет добавлен в справочник коэффициентов"
                    />
                )}
                <Grid rowGap="s" cols="1">
                    <GridItem>
                        <ControlledSelectField
                            name="ratioType"
                            label="Тип коэффициента"
                            control={control}
                            placeholder="Выберите"
                            items={typesItems}
                            disabled={!!id || isRatioTypesDisabled}
                            required
                            rules={requiredBaseRule}
                        />
                    </GridItem>
                    {!!id && (
                        <GridItem>
                            <ControlledTextField name="code" label="Шифр" control={control} type="text" disabled />
                        </GridItem>
                    )}
                    <GridItem>
                        <ControlledSelectField
                            name="catalogRatioGroupId"
                            label="Раздел"
                            control={control}
                            placeholder={!!id || isRatioGroupsDisabled ? '' : 'Выберите'}
                            items={groupsItems}
                            disabled={!!id || isRatioGroupsDisabled}
                            rules={catalogRatioGroupIdRules}
                            required
                        />
                    </GridItem>
                    <GridItem>
                        <ControlledTextField
                            name="name"
                            label="Наименование"
                            control={control}
                            placeholder="Введите"
                            type="textarea"
                            rules={{
                                ...requiredBaseRule,
                                ...validationRules.name,
                                maxLength: {
                                    value: 1500,
                                    message: 'Не более 1500 символов',
                                },
                            }}
                            required
                            maxRows={3}
                        />
                    </GridItem>
                    <GridItem>
                        <ControlNumberField
                            name="kozp"
                            label="К озп"
                            control={control}
                            placeholder="Введите"
                            type="number"
                            labelPosition="left"
                            width="full"
                            size="s"
                            required
                            rules={{ ...numericRatioFieldRules, ...validationRules.kozp }}
                            decimalScale={4}
                        />
                    </GridItem>
                    <GridItem>
                        <ControlNumberField
                            name="kem"
                            label="К эмм"
                            control={control}
                            placeholder="Введите"
                            type="number"
                            labelPosition="left"
                            width="full"
                            size="s"
                            required
                            rules={{ ...numericRatioFieldRules, ...validationRules.kem }}
                            decimalScale={4}
                        />
                    </GridItem>
                    <GridItem>
                        <ControlNumberField
                            name="knr"
                            label="К нр"
                            control={control}
                            placeholder="Введите"
                            type="number"
                            labelPosition="left"
                            width="full"
                            size="s"
                            required
                            rules={{ ...numericRatioFieldRules, ...validationRules.knr }}
                            decimalScale={4}
                        />
                    </GridItem>
                    <GridItem>
                        <ControlNumberField
                            name="ksp"
                            label="К сп"
                            control={control}
                            placeholder="Введите"
                            type="number"
                            labelPosition="left"
                            width="full"
                            size="s"
                            required
                            rules={{ ...numericRatioFieldRules, ...validationRules.ksp }}
                            decimalScale={4}
                        />
                    </GridItem>
                    <GridItem>
                        <ControlNumberField
                            name="kmat"
                            label="К мат"
                            control={control}
                            placeholder="Введите"
                            type="number"
                            labelPosition="left"
                            width="full"
                            size="s"
                            required={!isDismantlingCoef}
                            rules={{
                                ...validationRules.kmat,
                                ...getNumericRatioFieldRules({
                                    isRequired: !isDismantlingCoef,
                                    allowZero: isDismantlingCoef,
                                }),
                            }}
                            decimalScale={4}
                        />
                    </GridItem>
                    <GridItem>
                        <ControlNumberField
                            name="ktz"
                            label="К тз озп"
                            control={control}
                            placeholder="Введите"
                            type="number"
                            labelPosition="left"
                            width="full"
                            size="s"
                            required
                            rules={{ ...numericRatioFieldRules, ...validationRules.ktz }}
                            decimalScale={4}
                        />
                    </GridItem>
                    <GridItem>
                        <ControlNumberField
                            name="ktzzpm"
                            label="К тз зпм"
                            control={control}
                            placeholder="Введите"
                            type="number"
                            labelPosition="left"
                            width="full"
                            size="s"
                            required
                            rules={{ ...numericRatioFieldRules, ...validationRules.ktzzpm }}
                            decimalScale={4}
                        />
                    </GridItem>
                    {!!id && (
                        <GridItem>
                            <ControlledTextField
                                name="justification"
                                label="Обоснование"
                                control={control}
                                placeholder="Введите"
                                type="textarea"
                                required
                                rules={{
                                    ...justificationRules,
                                    ...validationRules.justification,
                                    maxLength: {
                                        value: 512,
                                        message: 'Не более 512 символов',
                                    },
                                }}
                                maxRows={3}
                            />
                        </GridItem>
                    )}
                </Grid>
            </div>
            <ConfirmationDialog
                isOpened={isOpenConfirmationDialog}
                onSubmit={handleActionQuery}
                onClose={handleCloseConfirmationDialog}
                textHeader="Отмена действия"
                textConfirmation="Вы уверены, что хотите отменить? Введенные данные не будут сохранены"
                cancelButtonText="Не отменять"
                confirmButtonText="Отменить"
            />
        </ModalDialog>
    );
};

CreateOrEditRatioModal.displayName = 'CreateOrEditRatioModal';
